import React from "react";

/**
 * A safer version of useStorage that properly syncs when the key changes.
 * This is useful when you need to switch between different storage keys dynamically.
 *
 * Unlike useStorage, this hook will:
 * - Re-read from storage whenever the key changes
 * - Properly clean up and sync state when switching between keys
 *
 * @param {string} key - The storage key (can change dynamically)
 * @param {*} initialValue - Default value if key doesn't exist in storage
 * @param {Storage} storageObject - window.localStorage or window.sessionStorage
 * @returns {[value, setValue, remove]} - Same API as useStorage
 */
const useStorageByKey = (key, initialValue, storageObject) => {
    // State to track the current value
    const [value, setValue] = React.useState(() => {
        const storedValue =
            storageObject === window.sessionStorage
                ? sessionStorage.getItem(key)
                : localStorage.getItem(key);
        return storedValue !== null ? JSON.parse(storedValue) : initialValue;
    });

    // Track the previous key to detect changes
    const prevKeyRef = React.useRef(key);

    // Effect to sync state when key changes
    React.useEffect(() => {
        // If the key has changed, read the new value from storage
        if (prevKeyRef.current !== key) {
            const storedValue =
                storageObject === window.sessionStorage
                    ? sessionStorage.getItem(key)
                    : localStorage.getItem(key);

            const newValue =
                storedValue !== null ? JSON.parse(storedValue) : initialValue;
            setValue(newValue);
            prevKeyRef.current = key;
        }
    }, [key, initialValue, storageObject]);

    // Effect to persist value to storage whenever it changes
    React.useEffect(() => {
        if (value === undefined) {
            storageObject.removeItem(key);
        } else {
            storageObject.setItem(key, JSON.stringify(value));
        }
    }, [key, value, storageObject]);

    const remove = React.useCallback(() => setValue(undefined), []);

    return [value, setValue, remove];
};

export default useStorageByKey;

// Usage example:
// const [executionId, setExecutionId] = useStorageByKey(
//     `executionId-${workflowId}`,
//     null,
//     window.localStorage
// );
// When workflowId changes, the hook will automatically read the new key's value
